import { useEffect, useRef, useState } from 'react';

function PageTest() {
  const glbPath = `${process.env.PUBLIC_URL}/movie/3d(m0922-2).glb`;
  const viewerRef = useRef(null);

  // 💡 1. 바디 구조물(body_object) 작동 상태 제어 (true: 원본값 유지, false: 전체 반투명 회색톤)
  const [isBodyActive, setIsBodyActive] = useState(true);

  // 💡 2. 느낌표(alert1, alert2) 가시성 제어 (true: 보임, false: 안보임)
  const [visibleAlerts, setVisibleAlerts] = useState({
    alert1: true,
    alert2: true,
  });

  // 💡 3. 느낌표(alert1, alert2) 색상 제어 ('red', 'orange', 'yellow', 'blue')
  const [alertColors, setAlertColors] = useState({
    alert1: 'red',
    alert2: 'red',
  });

  // 💡 구체(exclamation) 가시성 제어
  const [showExclamation, setShowExclamation] = useState(true);

  // 💡 각 화살표별 가시성 제어
  const [visibleArrows, setVisibleArrows] = useState({
    right: false,
    left: false,
    front: false,
    back: false,
    up: true,
    down: true,
  });

  // 색상 이름 매핑 (RGB 값)
  const COLOR_MAP = {
    red: [1, 0, 0],
    orange: [1, 0.5, 0],
    yellow: [1, 1, 0],
    blue: [0, 0, 1],
  };

  // 3D 모델 제어 및 애니메이션 루프
  useEffect(() => {
    const modelViewer = viewerRef.current;
    if (!modelViewer) return;

    let animationFrameId;
    let time = 0;

    const handleModelLoad = () => {
      const model = modelViewer.model;
      if (!model) return;

      const arrowDataList = [];
      const alertDataList = [];
      let sphereMaterial = null;

      // 1. 머티리얼 기본 1차 분류 및 필터링
      model.materials.forEach((material) => {
        if (!material || !material.pbrMetallicRoughness) return;

        const matName = material.name ? material.name.toLowerCase() : '';

        // [alert1, alert2 느낌표 제어]
        if (matName.includes('alert1') || matName.includes('alert2')) {
          const alertKey = matName.includes('alert1') ? 'alert1' : 'alert2';

          if (visibleAlerts[alertKey]) {
            material.setAlphaMode('BLEND');
            alertDataList.push({
              material: material,
              key: alertKey,
            });
          } else {
            material.setAlphaMode('BLEND');
            material.pbrMetallicRoughness.setBaseColorFactor([0, 0, 0, 0]);
          }
        }
        // [exclamation 구체 제어]
        else if (matName.includes('exclamation')) {
          sphereMaterial = material;
          if (showExclamation) {
            material.setAlphaMode('BLEND');
            material.pbrMetallicRoughness.setBaseColorFactor([1, 1, 0, 0.8]);
          } else {
            material.setAlphaMode('BLEND');
            material.pbrMetallicRoughness.setBaseColorFactor([0, 0, 0, 0]);
          }
        }
        // [화살표 머티리얼 초기 투명화]
        else if (matName.includes('arrow')) {
          material.setAlphaMode('BLEND');
          material.pbrMetallicRoughness.setBaseColorFactor([0, 0, 0, 0]);
        }
        // [일반 바디 및 기타 모든 구조물 머티리얼]
        else if (!isBodyActive) {
          // 비작동 상태일 때 특수 메쉬(alert, exclamation, arrow)를 제외한 모든 머티리얼 회색톤 변환
          material.setAlphaMode('BLEND');
          material.pbrMetallicRoughness.setBaseColorFactor([0.4, 0.4, 0.4, 0.3]);
        }
      });

      // 2. Three.js Scene Graph를 순회하며 body_object 노드 계층 및 화살표 탐색
      const sceneSymbol = Object.getOwnPropertySymbols(modelViewer).find(
        (sym) => sym.description === 'scene' || sym.toString().includes('scene')
      );
      const scene = sceneSymbol ? modelViewer[sceneSymbol] : null;

      if (scene) {
        scene.traverse((object) => {
          if (!object) return;

          const name = object.name ? object.name.toLowerCase() : '';

          // body_object 노드가 있고 비작동 상태인 경우 하위 메쉬 머티리얼 회색톤 변환
          if ((name.includes('body_object') || name.includes('body')) && !isBodyActive) {
            object.traverse((child) => {
              if (child.isMesh && child.material) {
                const targetMat = model.materials.find((m) => m.name === child.material.name);
                if (targetMat) {
                  targetMat.setAlphaMode('BLEND');
                  targetMat.pbrMetallicRoughness.setBaseColorFactor([0.4, 0.4, 0.4, 0.3]);
                }
              }
            });
          }

          // 화살표 방향 추출 및 등록
          if (object.isMesh || object.isGroup) {
            let direction = null;
            if (name.includes('arrow_right')) direction = 'right';
            else if (name.includes('arrow_left')) direction = 'left';
            else if (name.includes('arrow_front')) direction = 'front';
            else if (name.includes('arrow_back')) direction = 'back';
            else if (name.includes('arrow_up')) direction = 'up';
            else if (name.includes('arrow_down')) direction = 'down';

            if (direction) {
              const targetMat = model.materials.find(
                (m) => m.name && m.name.toLowerCase().includes(name)
              );

              if (targetMat && visibleArrows[direction]) {
                targetMat.setAlphaMode('BLEND');

                arrowDataList.push({
                  node: object,
                  material: targetMat,
                  direction: direction,
                  initialPos: object.position.clone(),
                });
              }
            }
          }
        });
      }

      // 애니메이션 루프
      const animate = () => {
        time += 0.08;

        // 1. [alert1, alert2] 이동 없이 알파(깜빡임) 및 색상 제어
        const alertAlpha = (Math.sin(time * 1.8) + 1) / 2;
        alertDataList.forEach(({ material, key }) => {
          if (material && material.pbrMetallicRoughness) {
            const rgb = COLOR_MAP[alertColors[key]] || COLOR_MAP.orange;
            material.pbrMetallicRoughness.setBaseColorFactor([
              rgb[0],
              rgb[1],
              rgb[2],
              alertAlpha,
            ]);
          }
        });

        // 2. [화살표] 방향별 색상(up: 빨강, down: 파랑) + 위치 이동 + 알파 깜빡임
        const arrowAlpha = (Math.sin(time * 1.5) + 1) / 2;
        const offset = (Math.sin(time * 2.0) + 1) * 0.3;

        arrowDataList.forEach(({ node, material, direction, initialPos }) => {
          if (material && material.pbrMetallicRoughness) {
            // 💡 방향에 따른 화살표 색상 지정 (up: 빨간색, down: 파란색, 기타: 파란색)
            const arrowRgb = direction === 'up' ? [1, 0, 0] : [0, 0, 1];
            material.pbrMetallicRoughness.setBaseColorFactor([
              arrowRgb[0],
              arrowRgb[1],
              arrowRgb[2],
              arrowAlpha,
            ]);
          }

          if (node) {
            node.position.copy(initialPos);
            switch (direction) {
              case 'right':
                node.position.x += offset;
                break;
              case 'left':
                node.position.x -= offset;
                break;
              case 'front':
                node.position.z += offset;
                break;
              case 'back':
                node.position.z -= offset;
                break;
              case 'up':
                node.position.y += offset;
                break;
              case 'down':
                node.position.y -= offset;
                break;
              default:
                break;
            }
          }
        });

        // 3. [구체] 깜빡임
        if (showExclamation && sphereMaterial && sphereMaterial.pbrMetallicRoughness) {
          const pulse = Math.pow(Math.max(0, Math.sin(time * 2.2)), 3);
          const sphereAlpha = 0.2 + pulse * 0.75;
          const currentColor = sphereMaterial.pbrMetallicRoughness.baseColorFactor;

          sphereMaterial.pbrMetallicRoughness.setBaseColorFactor([
            currentColor[0],
            currentColor[1],
            currentColor[2],
            sphereAlpha,
          ]);
        }

        animationFrameId = requestAnimationFrame(animate);
      };

      animate();
    };

    if (modelViewer.loaded) {
      handleModelLoad();
    } else {
      modelViewer.addEventListener('load', handleModelLoad);
    }

    return () => {
      modelViewer.removeEventListener('load', handleModelLoad);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isBodyActive, showExclamation, visibleArrows, visibleAlerts, alertColors]);

  return (
    <div style={{ width: '100vw', height: '100vh', backgroundColor: '#1a1a1a' }}>
      <model-viewer
        ref={viewerRef}
        src={glbPath}
        ar
        camera-controls
        touch-action="pan-y"
        alt="A 3D model"
        style={{
          width: '100%',
          height: '100%',
        }}
      ></model-viewer>
    </div>
  );
}

export default PageTest;